import React, { useMemo } from "react";
import type { ListingPageV2Params, MyCashState } from "../../ListingPageV2.types";
import { ListingCarouselPersuasion } from "../ListingCarouselPersuasion/ListingCarouselPersuasion";
import { PlanCard, type PlanCardProps } from "../PlanCard/PlanCard";

interface PlanCardData {
  type: "PLAN_CARD";
  data: PlanCardProps;
}

interface CarouselPersuasionData {
  type: "CAROUSEL_PERSUASION";
  data: { cards?: Array<{ icon?: string; title?: string; subtitle?: string }> };
}

type CardItem = PlanCardData | CarouselPersuasionData;

export interface PlanCardListProps {
  cards?: CardItem[];
  onCompareToggle?: (planId: string, selected: boolean) => void;
  isPlanCompared?: (planId: string) => boolean;
  myCashState?: MyCashState;
  isNetPricing?: boolean;
  sortedQuoteIds?: string[] | null;
  selectedPlanIds?: string[] | null;
  listingParams?: ListingPageV2Params;
  csrfToken?: string;
  onBuyNow?: PlanCardProps["onBuyNow"];
  onTncOpen?: PlanCardProps["onTncOpen"];
}

export const PlanCardList = ({
  cards,
  onCompareToggle,
  isPlanCompared,
  myCashState = "unApplied",
  isNetPricing = false,
  sortedQuoteIds,
  selectedPlanIds,
  csrfToken,
  onBuyNow,
  onTncOpen,
}: PlanCardListProps) => {
  const processedCards = useMemo(() => {
    if (!cards?.length) return [];

    if (!selectedPlanIds?.length && !sortedQuoteIds?.length) return cards;

    let planCards = cards.filter(
      (c): c is PlanCardData => c.type === "PLAN_CARD"
    );
    const otherCards = cards.filter((c) => c.type !== "PLAN_CARD");

    if (selectedPlanIds?.length) {
      const idSet = new Set(selectedPlanIds);
      planCards = planCards.filter(
        (c) => c.data.planId && idSet.has(c.data.planId)
      );
    }

    if (sortedQuoteIds?.length) {
      const orderMap = new Map(sortedQuoteIds.map((id, i) => [id, i]));
      planCards = [...planCards].sort((a, b) => {
        const aIdx = orderMap.get(a.data.planId || "") ?? Infinity;
        const bIdx = orderMap.get(b.data.planId || "") ?? Infinity;
        return aIdx - bIdx;
      });
    }

    const result: CardItem[] = [...(planCards as CardItem[])];
    otherCards.forEach((c) => result.push(c));
    return result;
  }, [cards, sortedQuoteIds, selectedPlanIds]);

  if (!processedCards.length) return null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {processedCards.map((card, index) => {
        if (card.type === "PLAN_CARD") {
          const planData = card.data;
          const prices = !isNetPricing
            ? planData.prices
            : planData.prices
              ? {
                  ...planData.prices,
                  discountedPrice:
                    planData.prices.originalPrice ||
                    planData.prices.discountedPrice,
                  originalPrice: undefined,
                }
              : undefined;

          return (
            <PlanCard
              key={planData.planId || `plan-${index}`}
              planId={planData.planId}
              productId={planData.productId}
              badge={planData.badge}
              icon={planData.icon}
              planIcon={planData.planIcon}
              subtitle={planData.subtitle}
              title={planData.title}
              prices={prices}
              unit={planData.unit}
              headerPersuation={isNetPricing ? undefined : planData.headerPersuation}
              previewBenefits={planData.previewBenefits}
              cta={planData.cta}
              showCompare={planData.showCompare}
              footerPersuation={planData.footerPersuation}
              benefitsBottomsheet={planData.benefitsBottomsheet}
              downloadBottomsheet={planData.downloadBottomsheet}
              csrfToken={csrfToken}
              onCompareToggle={onCompareToggle}
              isCompareSelected={isPlanCompared?.(planData.planId || "")}
              myCashState={myCashState}
              onBuyNow={onBuyNow}
              onTncOpen={onTncOpen}
            />
          );
        }
        if (card.type === "CAROUSEL_PERSUASION") {
          return (
            <ListingCarouselPersuasion
              key={`carousel-${index}`}
              cards={card.data.cards}
            />
          );
        }
        return null;
      })}
    </div>
  );
};
